import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MapPinIcon, UsersIcon, BuildingIcon, FilterIcon, XIcon, MapIcon, ChevronDownIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { AGE_BRACKETS, DEPARTMENTS, RESIDENCE_OPTIONS, type Coord } from '../../data/communications';
import { countActiveFilters, summarizePlaces } from '../../utils/audience';
import { AudienceMap } from './AudienceMap';
import { AudiencePlaces } from './AudiencePlaces';
import { AudienceFilters } from './AudienceFilters';
import type { AudiencePlace, AudienceState } from '../../types/composer';
interface AudienceSelectorProps {
  audience: AudienceState;
  onChange: (patch: Partial<AudienceState>) => void;
  audienceTotal: number;
}

/**
 * Audience = Lieu (whole city, or places that add up) × Qui (filters that
 * narrow it down). The summary bar spells out the combination.
 */
export function AudienceSelector({
  audience,
  onChange,
  audienceTotal
}: AudienceSelectorProps) {
  const {
    t,
    lang
  } = useLanguage();
  const locale = lang === 'fr' ? 'fr-CA' : 'en-CA';
  const [drawing, setDrawing] = useState(false);
  const [draftPoints, setDraftPoints] = useState<Coord[]>([]);
  const [showMap, setShowMap] = useState(false);
  const {
    scope,
    places
  } = audience;
  const filtersCount = countActiveFilters(audience);
  const setPlaces = (next: AudiencePlace[]) => onChange({
    places: next
  });
  const toggleSector = (sectorId: string) => {
    const existing = places.find(p => p.kind === 'sector' && p.sectorId === sectorId);
    setPlaces(existing ? places.filter(p => p.id !== existing.id) : [...places, {
      id: `pl-${sectorId}-${Date.now()}`,
      kind: 'sector',
      sectorId
    }]);
  };
  const finishDrawing = () => {
    if (draftPoints.length >= 3) {
      setPlaces([...places, {
        id: `pl-area-${Date.now()}`,
        kind: 'area',
        points: draftPoints
      }]);
    }
    setDraftPoints([]);
    setDrawing(false);
  };
  const cancelDrawing = () => {
    setDraftPoints([]);
    setDrawing(false);
  };

  // Values of one criterion are OR-ed; different criteria are AND-ed.
  const orSep = lang === 'fr' ? ' ou ' : ' or ';

  // Filter chips shown in the summary, each removable in one click.
  const filterChips: {
    key: string;
    label: string;
    clear: () => void;
  }[] = [...(audience.selectedAges.length > 0 ? [{
    key: 'age',
    label: AGE_BRACKETS.filter(a => audience.selectedAges.includes(a.id)).map(a => a.label[lang]).join(orSep),
    clear: () => onChange({
      selectedAges: []
    })
  }] : []), ...(audience.selectedDepartments.length > 0 ? [{
    key: 'dep',
    label: DEPARTMENTS.filter(d => audience.selectedDepartments.includes(d.id)).map(d => d.label[lang]).join(orSep),
    clear: () => onChange({
      selectedDepartments: []
    })
  }] : []), ...(audience.residence !== 'all' ? [{
    key: 'res',
    label: RESIDENCE_OPTIONS.find(r => r.value === audience.residence)?.label[lang] ?? '',
    clear: () => onChange({
      residence: 'all'
    })
  }] : [])];
  const whereLabel = scope === 'all' ? t('Toute la ville', 'Entire city') : places.length === 0 ? t('Aucun lieu', 'No place') : summarizePlaces(places, lang);
  const scopeOptions: {
    id: AudienceState['scope'];
    icon: typeof UsersIcon;
    label: string;
    hint: string;
  }[] = [{
    id: 'all',
    icon: BuildingIcon,
    label: t('Toute la ville', 'Entire city'),
    hint: t('Tous les citoyens inscrits', 'Every registered citizen')
  }, {
    id: 'places',
    icon: MapPinIcon,
    label: t('Lieux précis', 'Specific places'),
    hint: t('Secteurs, adresses, rayons ou zones', 'Districts, addresses, radii or areas')
  }];
  return <section className="space-y-6" data-id="element-3845" data-mp="197izq8i1r3gd">
      <h2 className="text-xl font-semibold text-slate-900" data-id="element-3846" data-mp="198tz7w4glp1o">{t('Audience', 'Audience')}</h2>

      <div className="bg-white rounded-xl border-[0.5px] border-slate-200 divide-y divide-slate-100" data-id="element-3847" data-mp="15446uh90esli">
        {/* 1 — WHERE */}
        <div className="p-6 space-y-4" data-id="element-3848" data-mp="108feoyzbkdz8">
          <div className="flex items-baseline gap-2" data-id="element-3849" data-mp="v8ayla2bpl9j">
            <span className="text-xs font-bold text-[#4F46E5] bg-indigo-50 w-5 h-5 rounded-full inline-flex items-center justify-center" data-id="element-3850" data-mp="v9ue2yg5nsa9">
              1
            </span>
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-3851" data-mp="1nwounk9uq7ig">{t('Où', 'Where')}</h3>
            {scope === 'places' && places.length > 1 && <span className="text-xs text-slate-400" data-id="element-3852" data-mp="1e6n66rdl6n30">
                {t('Personne située dans au moins une de ces zones', 'Anyone located in at least one of these areas')}
              </span>}
          </div>

          <div className="inline-flex rounded-lg border border-slate-200 bg-slate-50 p-1" role="radiogroup" aria-label={t('Où', 'Where')} data-id="element-3853" data-mp="26t7wdoibst4z">
            {scopeOptions.map(o => {
            const Icon = o.icon;
            const active = scope === o.id;
            return <button key={o.id} type="button" role="radio" aria-checked={active} title={o.hint} onClick={() => {
              if (o.id === 'all') cancelDrawing();
              onChange({
                scope: o.id
              });
            }} className={`flex items-center gap-2 px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${active ? 'bg-white text-[#4F46E5] shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-3854" data-mp="2t3zm5gnlsodx">
                  <Icon className="w-4 h-4" data-id="element-3855" data-mp="3ktui3w5yvcbk" />
                  {o.label}
                </button>;
          })}
          </div>

          <AnimatePresence initial={false} data-id="element-3856" data-mp="1lnrslwpwv9u0">
            {scope === 'places' && <motion.div key="places" initial={{
            height: 0,
            opacity: 0,
            overflow: 'hidden'
          }} animate={{
            height: 'auto',
            opacity: 1,
            // Let the "Ajouter un lieu" dropdown overflow once expanded.
            transitionEnd: {
              overflow: 'visible'
            }
          }} exit={{
            height: 0,
            opacity: 0,
            overflow: 'hidden'
          }} transition={{
            duration: 0.2
          }} className="relative z-20" data-id="element-3857" data-mp="1gsci9er43w2c">
                <div className="pt-1 space-y-4" data-id="element-3858" data-mp="29fq86swe7rcy">
                  <AudiencePlaces places={places} onChange={setPlaces} onToggleSector={toggleSector} drawing={drawing} draftPoints={draftPoints} onStartDrawing={() => {
                setDraftPoints([]);
                setDrawing(true);
              }} onCancelDrawing={cancelDrawing} onFinishDrawing={finishDrawing} data-id="element-3859" data-mp="29fyn0qc2t6qs" />
                  {(places.length > 0 || drawing) && <div className="space-y-2" data-id="element-3860" data-mp="5qtast5zqp5">
                      {!drawing && <button type="button" onClick={() => setShowMap(s => !s)} aria-expanded={showMap} className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#4F46E5] transition-colors" data-id="element-3861" data-mp="3hemh647mzdfu">
                          <MapIcon className="w-3.5 h-3.5" data-id="element-3862" data-mp="3df5a56cka88k" />
                          {showMap ? t('Masquer la carte', 'Hide map') : t('Voir sur la carte', 'Show on map')}
                          <ChevronDownIcon className={`w-3.5 h-3.5 transition-transform ${showMap ? 'rotate-180' : ''}`} data-id="element-3863" data-mp="1blfi7ppzazsl" />
                        </button>}
                      {(showMap || drawing) && <AudienceMap places={places} onToggleSector={toggleSector} drawing={drawing} draftPoints={draftPoints} onAddDraftPoint={c => setDraftPoints(prev => [...prev, c])} data-id="element-3864" data-mp="2af8scco7xe8a" />}
                    </div>}
                </div>
              </motion.div>}
          </AnimatePresence>
        </div>

        {/* 2 — WHO */}
        <div className="p-6 space-y-4" data-id="element-3865" data-mp="1nign7xj8zln5">
          <div className="flex items-baseline gap-2" data-id="element-3866" data-mp="3lw04n7j5c7mw">
            <span className="text-xs font-bold text-[#4F46E5] bg-indigo-50 w-5 h-5 rounded-full inline-flex items-center justify-center" data-id="element-3867" data-mp="3ltm96xkk0tet">
              2
            </span>
            <h3 className="text-sm font-semibold text-slate-900" data-id="element-3868" data-mp="13luhbzz2tx6s">{t('Qui', 'Who')}</h3>
            <span className="text-xs text-slate-400" data-id="element-3869" data-mp="18k5rycx74bq2">{t('Facultatif', 'Optional')}</span>
          </div>
          <AudienceFilters audience={audience} onChange={onChange} data-id="element-3870" data-mp="1ddgnhhwk562o" />
        </div>

        {/* Summary */}
        <div className="px-6 py-4 bg-slate-50/70 rounded-b-xl flex flex-col md:flex-row md:items-center md:justify-between gap-3" data-id="element-3871" data-mp="1hd91wb8mu6fy">
          <div className="flex flex-wrap items-center gap-1.5 text-sm text-slate-600" data-id="element-3872" data-mp="kpunskqgall3">
            <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-white border border-slate-200 font-medium text-slate-700" data-id="element-3873" data-mp="pop4nh4uy02k">
              <MapPinIcon className="w-3.5 h-3.5 text-[#4F46E5]" data-id="element-3874" data-mp="ynj6pn0yjini" />
              {whereLabel}
            </span>
            {filterChips.map(chip => <React.Fragment key={chip.key} data-id="element-3875" data-mp="255pjylsddyf9">
                <span className="text-xs font-semibold uppercase text-slate-400 px-0.5" data-id="element-3876" data-mp="2a1mxkkc90qoc">
                  {t('et', 'and')}
                </span>
                <span className="inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-md bg-white border border-slate-200 font-medium text-slate-700 max-w-[260px]" data-id="element-3877" data-mp="16cwnn5rvvfy6">
                  <FilterIcon className="w-3.5 h-3.5 text-[#4F46E5] flex-shrink-0" data-id="element-3878" data-mp="1g653sfo2plpl" />
                  <span className="truncate" data-id="element-3879" data-mp="1l2viozo56b9f">{chip.label}</span>
                  <button type="button" onClick={chip.clear} aria-label={`${t('Retirer le filtre', 'Remove filter')} ${chip.label}`} className="p-0.5 rounded text-slate-400 hover:text-slate-700 hover:bg-slate-100" data-id="element-3880" data-mp="ng4zahxhian9">
                    <XIcon className="w-3 h-3" data-id="element-3881" data-mp="11gfozhn25j6u" />
                  </button>
                </span>
              </React.Fragment>)}
            {filtersCount === 0 && <span className="text-xs text-slate-400 ml-1" data-id="element-3882" data-mp="2cv2yjxvtpjmr">
                {t('· aucun filtre', '· no filter')}
              </span>}
          </div>
          <div className="flex items-center gap-2 flex-shrink-0" data-id="element-3883" data-mp="177u22zkg5hv">
            <UsersIcon className="w-4 h-4 text-[#4F46E5]" data-id="element-3884" data-mp="3tba5v0qqm32n" />
            <span className="text-sm text-slate-500" data-id="element-3885" data-mp="3tdwdt1hjikle">
              {audienceTotal > 0 && filtersCount > 0 ? t('environ', 'about') + ' ' : ''}
              <span className="text-lg font-bold text-[#0F172A]" data-id="element-3886" data-mp="3jknwho9m68se">
                {audienceTotal.toLocaleString(locale)}
              </span>{' '}
              {t('personnes ciblées', 'people targeted')}
            </span>
          </div>
        </div>
      </div>
    </section>;
}